// src/pages/Menu/Articulacion/Documents/ProjectMinuteModal.jsx
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { X, Info } from "lucide-react";
import { updateProjectMinute } from "../../../../../services/projectMinutesService";
import TooltipHelper from "../../../../../components/common/TooltipHelper";

/* === helpers fecha seguros para inputs === */
const isYMD = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ""));
const toYMDForInput = (v) => {
  if (!v) return "";
  if (isYMD(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(+d)) return "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};


/**
 * Modal para editar una minuta de proyecto:
 * - Cambiar título
 * - Cambiar fecha
 * - Reemplazar PDF (opcional)
 */
export default function ProjectMinuteModal({
  open,
  initial,
  onClose,
  onSaved,
  projectId,
}) {
  const [title, setTitle] = useState(initial?.title || "");
  const [date, setDate] = useState(toYMDForInput(initial?.date) || "");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || "");
    setDate(toYMDForInput(initial?.date) || "");
    setFile(null);
  }, [open, initial]);

  const canSave = title.trim() && !saving;

  const onSubmit = async () => {
    if (!canSave) return;

    // Validar que la fecha no sea futura (solo fecha actual hacia atrás)
    if (date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // Normalizar la fecha seleccionada a medianoche local
      const selectedDate = new Date(date + 'T00:00:00');
      selectedDate.setHours(0, 0, 0, 0);
      // Comparar solo las fechas (sin hora)
      if (selectedDate > today) {
        await Swal.fire({
          icon: "warning",
          title: "Fecha inválida",
          text: "No se puede actualizar una minuta con fecha futura. Solo se permiten fechas de hoy hacia atrás.",
        });
        return;
      }
    }

    if (file) {
      if (file.type !== "application/pdf") {
        await Swal.fire({
          icon: "warning",
          title: "Formato inválido",
          text: "Solo PDF.",
        });
        return;
      }
      if (file.size > 15 * 1024 * 1024) {
        await Swal.fire({
          icon: "warning",
          title: "Archivo muy grande",
          text: "Máx 15 MB.",
        });
        return;
      }
    }

    const payload = {
      title: title.trim(),
      date: date || undefined,
      ...(file ? { file } : {}),
    };

    try {
      setSaving(true);
      Swal.fire({
        title: "Guardando…",
        html: file
          ? "Subiendo PDF y actualizando datos…"
          : "Actualizando datos…",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      await updateProjectMinute(projectId, initial.id, payload);

      Swal.close();
      await Swal.fire({
        icon: "success",
        title: "Minuta actualizada",
        timer: 1100,
        showConfirmButton: false,
      });

      onSaved?.();
      onClose?.();
    } catch (e) {
      Swal.close();
      const errorMessage = e?.message || "Error desconocido al actualizar la minuta";
      await Swal.fire({
        icon: "error",
        title: "No se pudo actualizar",
        text: errorMessage,
        confirmButtonText: "Entendido",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[260] bg-black/40 grid place-items-center p-4"
      onMouseDown={(e) =>
        e.target === e.currentTarget && onClose?.()
      }
    >
      <div
        className="rounded-2xl p-[2px]"
        style={{ background: "linear-gradient(135deg,#E6F0FF 0%,#E6FBFF 100%)" }}
      >
        <div className="w-full max-w-2xl rounded-2xl overflow-hidden bg-white ring-1 ring-black/5">
          {/* Header */}
          <div
            className="px-5 py-4 text-white"
            style={{ background: "linear-gradient(90deg,#0c3b66,#0488AF)" }}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-base sm:text-lg font-semibold">Editar minuta</div>
                <div className="text-xs sm:text-sm opacity-90 mt-0.5">
                  Cambia el título/fecha o reemplaza el PDF.
                </div>
              </div>
              <button
                className="p-2 rounded-lg hover:bg-white/10"
                onClick={onClose}
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[72vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full rounded-xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none placeholder:text-gray-400"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej: Minuta reunión Comité de Innovación"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha (opcional)
            </label>
            <input
              type="date"
              className="w-full rounded-xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              Reemplazar PDF (opcional)
              <TooltipHelper text="Si deseas actualizar el PDF de la minuta, selecciona un nuevo archivo. El archivo anterior será reemplazado. Si no seleccionas un archivo, se mantendrá el PDF actual. Máximo 15 MB, solo formato PDF." />
            </label>
            <input
              type="file"
              accept="application/pdf"
              onChange={async (e) => {
                const selectedFile = e.target.files?.[0] || null;
                if (selectedFile) {
                  if (selectedFile.type !== 'application/pdf') {
                    await Swal.fire({
                      icon: 'error',
                      title: 'Formato inválido',
                      text: 'Debes subir un archivo PDF',
                    });
                    e.target.value = '';
                    return;
                  }
                  if (selectedFile.size > 15 * 1024 * 1024) {
                    await Swal.fire({
                      icon: 'warning',
                      title: 'Archivo muy grande',
                      html: `El archivo "${selectedFile.name}" es demasiado grande (máx 15 MB).<br/><br/>Por favor, comprime el archivo PDF antes de subirlo.<br/><br/><a href="https://www.ilovepdf.com/es/comprimir_pdf" target="_blank" rel="noopener noreferrer" style="color: #0488AF; text-decoration: underline;">Comprimir PDF aquí</a>`,
                      confirmButtonText: 'Entendido',
                    });
                    e.target.value = '';
                    return;
                  }
                }
                setFile(selectedFile);
              }}
              className="block w-full text-sm text-gray-700 file:mr-4 file:rounded-xl file:border-0 file:bg-[#0488AF] file:px-4 file:py-2 file:text-white hover:file:bg-[#037a98] transition-colors"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white flex justify-end gap-2">
          <button
            className="h-11 px-4 rounded-xl bg-[#C42251] text-white hover:bg-[#A01D45] text-sm font-semibold transition-colors"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            className={`h-11 px-4 rounded-xl text-white text-sm font-semibold transition-colors ${
              canSave
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-gray-400 cursor-not-allowed"
            }`}
            onClick={onSubmit}
            disabled={!canSave}
          >
            {saving ? (
              <span className="inline-flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
                Guardando…
              </span>
            ) : (
              "Guardar cambios"
            )}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}
